import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ReminderContext = createContext();

export function ReminderProvider({ children }) {
    const [reminders, setReminders] = useState(() => {
        const saved = localStorage.getItem('getiri-reminders');
        return saved ? JSON.parse(saved) : [];
    });
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        localStorage.setItem('getiri-reminders', JSON.stringify(reminders));
    }, [reminders]);

    // Check reminders periodically
    useEffect(() => {
        const checkReminders = () => {
            const now = new Date();
            reminders.forEach(reminder => {
                if (!reminder.notified) {
                    const reminderDate = new Date(reminder.date);
                    if (now >= reminderDate) {
                        showNotification(reminder);
                        markAsNotified(reminder.id);
                    }
                }
            });
        };

        const interval = setInterval(checkReminders, 60000); // Check every minute
        checkReminders(); // Initial check

        return () => clearInterval(interval);
    }, [reminders]);

    const showNotification = (reminder) => {
        setNotification({
            title: reminder.title,
            message: reminder.message,
            targetAmount: reminder.targetAmount
        });

        // Browser notification if permitted
        if (Notification.permission === 'granted') {
            new Notification('Getiri Hatırlatıcı', {
                body: `${reminder.title}: ${reminder.message}`,
                icon: '/logo192.png'
            });
        }

        // Auto-hide after 10 seconds
        setTimeout(() => setNotification(null), 10000);
    };

    const markAsNotified = (id) => {
        setReminders(prev => prev.map(r =>
            r.id === id ? { ...r, notified: true } : r
        ));
    };

    const addReminder = useCallback((reminder) => {
        const newReminder = {
            id: Date.now(),
            createdAt: new Date().toISOString(),
            notified: false,
            ...reminder
        };
        setReminders(prev => [...prev, newReminder]);
        return newReminder;
    }, []);

    const removeReminder = useCallback((id) => {
        setReminders(prev => prev.filter(r => r.id !== id));
    }, []);

    const requestNotificationPermission = async () => {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return false;
    };

    const dismissNotification = () => {
        setNotification(null);
    };

    return (
        <ReminderContext.Provider value={{
            reminders,
            notification,
            addReminder,
            removeReminder,
            requestNotificationPermission,
            dismissNotification
        }}>
            {children}
        </ReminderContext.Provider>
    );
}

export function useReminders() {
    const context = useContext(ReminderContext);
    if (!context) {
        throw new Error('useReminders must be used within ReminderProvider');
    }
    return context;
}
